import { Injectable, Injector } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpErrorResponse,
} from '@angular/common/http';
import { Router } from '@angular/router';
import { catchError, switchMap, throwError } from 'rxjs';
import { AuthService } from 'auth';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private authService?: AuthService;

  constructor(
    private injector: Injector,
    private router: Router,
  ) { }

  intercept(req: HttpRequest<unknown>, next: HttpHandler) {
    if (!this.authService) {
      this.authService = this.injector.get(AuthService);
    }
    const accessToken = this.authService.getAccessToken();
    let authReq = req;
    const url = req.url.toLowerCase();
    const authenticatedAuthRoutes = [
      '/authentication/logout',
      '/authentication/change-password/confirm',
      '/authentication/change-password',
    ];
    const isPublicAuthRequest =
      url.includes('/authentication/') &&
      !authenticatedAuthRoutes.some((route) => url.includes(route));

    if (accessToken && !isPublicAuthRequest) {
      authReq = req.clone({
        setHeaders: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
    }

    return next.handle(authReq).pipe(
      catchError((err: HttpErrorResponse) => {
        // The refresh-token call's own 401 IS the refresh failure — never
        // re-enter refresh logic in response to it, or this recurses back
        // into itself through this same interceptor.
        if (err.status === 401 && url.includes('/authentication/refresh-token')) {
          return throwError(() => err);
        }

        // Don't try token refresh during logout — just fail and let local logout finish.
        if (err.status === 401 && url.includes('/authentication/logout')) {
          return throwError(() => err);
        }

        if (err.status === 401) {
          return this.authService!.getRefreshedToken().pipe(
            switchMap((newAccessToken) => {
              const retryReq = req.clone({
                setHeaders: {
                  Authorization: `Bearer ${newAccessToken}`,
                },
              });
              return next.handle(retryReq);
            }),
            catchError((refreshErr) => {
              if (this.authService!.isInvalidRefreshTokenError(refreshErr)) {
                this.authService!.clearTokens();
                this.router.navigate(['/login']);
              }
              return throwError(() => err);
            }),
          );
        }

        return throwError(() => err);
      }),
    );
  }
}
